import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { Chip, EmptyState, Skeleton, Table, TBody, TD, TH, THead, TR } from '../components/ui'
import { useDevices, useDoors, useLocationTree, useSystems } from '../api/hooks'
import { locationPaths } from '../lib/locations'
import { LocationLink } from '../components/LocationLink'
import type { Device } from '../api/types'

/** Same pill idiom as the Events page severity filter. */
function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-sm border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
        active
          ? 'border-ostrel-navy bg-ostrel-navy text-ostrel-light'
          : 'border-ostrel-line bg-ostrel-card text-ostrel-ink-muted hover:bg-ostrel-sand'
      }`}
    >
      {label}
    </button>
  )
}

/** Placement breadcrumb: the door's path when placed on a door, else the
 *  location path (linked), else '—'. */
function placement(
  d: Device,
  doorById: Map<string, { name: string; breadcrumb: string[] }>,
  paths: Map<string, string>,
): ReactNode {
  if (d.door) {
    const door = doorById.get(d.door)
    if (door) return [...door.breadcrumb, door.name].join(' / ')
  }
  if (d.location) return <LocationLink uid={d.location}>{paths.get(d.location) ?? d.location}</LocationLink>
  return '—'
}

/** Every device across the customer's systems (read-only — devices are
 *  connector-managed; placement and adoption happen in the mapping
 *  workbench). The system filter is a chip row, same idiom as Events. */
export default function DevicesPage() {
  const devices = useDevices()
  const systems = useSystems()
  const doors = useDoors()
  const locationTree = useLocationTree()
  const [systemFilter, setSystemFilter] = useState<string>('all')

  const systemNames = useMemo(
    () => new Map((systems.data ?? []).map((s) => [s.unique_id, s.name])),
    [systems.data],
  )
  const doorById = useMemo(
    () => new Map((doors.data ?? []).map((d) => [d.unique_id, { name: d.name, breadcrumb: d.breadcrumb }])),
    [doors.data],
  )
  const paths = useMemo(() => locationPaths(locationTree.data ?? []), [locationTree.data])

  const filtered = (devices.data ?? []).filter((d) => systemFilter === 'all' || d.system === systemFilter)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Connections" title="Devices" />

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          System
        </span>
        <FilterChip active={systemFilter === 'all'} label="All" onClick={() => setSystemFilter('all')} />
        {(systems.data ?? []).map((s) => (
          <FilterChip
            key={s.unique_id}
            active={systemFilter === s.unique_id}
            label={s.name}
            onClick={() => setSystemFilter(systemFilter === s.unique_id ? 'all' : s.unique_id)}
          />
        ))}
      </div>

      {devices.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : devices.isError ? (
        <EmptyState title="Could not load devices" hint={devices.error.message} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={devices.data.length === 0 ? 'No devices yet' : 'No devices match the filter'}
          hint="Devices appear after a system syncs its hardware."
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Device</TH>
              <TH>Kind</TH>
              <TH>System</TH>
              <TH>Online</TH>
              <TH>Placement</TH>
              <TH>Vendor ref</TH>
            </tr>
          </THead>
          <TBody>
            {filtered.map((d) => (
              <TR key={d.unique_id}>
                <TD>
                  <Link
                    to={`/devices/${d.unique_id}`}
                    className="block truncate text-sm font-medium text-ostrel-ink hover:text-ostrel-accent"
                  >
                    {d.name}
                  </Link>
                  <span className="tnum block font-mono text-[10px] text-ostrel-ink-muted">
                    {d.unique_id}
                  </span>
                </TD>
                <TD>
                  <Chip tone="muted" label={d.kind.replace(/_/g, ' ')} />
                </TD>
                <TD>
                  <Link
                    to={`/systems/${d.system}`}
                    className="text-xs text-ostrel-ink-muted hover:text-ostrel-accent"
                  >
                    {systemNames.get(d.system) ?? d.system}
                  </Link>
                </TD>
                <TD>
                  <span
                    title={d.online === null ? 'unknown' : d.online ? 'online' : 'offline'}
                    className={`inline-block h-2 w-2 rounded-full ${
                      d.online === null ? 'bg-ostrel-dim' : d.online ? 'bg-ostrel-ok' : 'bg-ostrel-danger'
                    }`}
                  />
                </TD>
                <TD>
                  <span className="flex items-center gap-2">
                    <span className="tnum block max-w-60 truncate text-xs text-ostrel-ink-muted">
                      {placement(d, doorById, paths)}
                    </span>
                    <Link
                      to={`/devices/${d.unique_id}`}
                      className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
                    >
                      Edit
                    </Link>
                  </span>
                </TD>
                <TD>
                  <span className="tnum font-mono text-[11px] text-ostrel-ink-muted">
                    {d.vendor_ref || '—'}
                  </span>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  )
}
